import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { useToast } from '../context/ToastContext';
import { ensureAuthenticated } from '../services/authGuardService';
import { getStoredUser } from '../services/subscriptionService';
import { getApiErrorMessage, readJsonSafe } from '../services/apiResponseService';
import headerStyles from '../styles/modules/Header.module.css';
import pageStyles from '../styles/modules/PageLayout.module.css';
import styles from '../styles/modules/CreateJobOffer.module.css';
import backgroundImage from '../images/background.jpg';

const CreateJobOffer: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const storedUser = getStoredUser();
    const [dateType, setDateType] = useState<'singleDay' | 'period'>('singleDay');
    const [formData, setFormData] = useState({
        jobRole: '',
        facilityName: (storedUser as { businessName?: string | null } | null)?.businessName || '',
        numberOfStaff: '1',
        date: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        location: '',
        latitude: 44.816437,
        longitude: 20.457273,
        payForTransport: false,
        description: '',
        payRate: ''
    });
    const [showMapModal, setShowMapModal] = useState(false);
    const [tempCoords, setTempCoords] = useState({ lat: 44.816437, lng: 20.457273 });
    const [isGeocoding, setIsGeocoding] = useState(false);

    const staffRoles = [
        'Barman',
        'Bartender',
        'Waiter',
        'Waitress',
        'Chef',
        'Sous Chef',
        'Cook',
        'Dishwasher',
        'Host/Hostess',
        'Sommelier',
        'Busser',
        'Kitchen Porter',
        'Line Cook',
        'Pastry Chef'
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // If location input changes, try to geocode the address (with debounce)
        if (name === 'location' && value.length > 3) {
            setIsGeocoding(true);
            // Clear any existing timeout
            if ((window as any).geocodeTimeout) {
                clearTimeout((window as any).geocodeTimeout);
            }
            // Set new timeout for geocoding
            (window as any).geocodeTimeout = setTimeout(() => {
                geocodeAddress(value);
            }, 1000); // Wait 1 second after user stops typing
        }
    };

    const geocodeAddress = async (address: string) => {
        try {
            console.log('Geocoding address:', address);
            // Using Nominatim (OpenStreetMap) for geocoding - free and no API key needed
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
                {
                    headers: {
                        'User-Agent': 'BarmenFinder/1.0' // Nominatim requires a user agent
                    }
                }
            );
            const data = await response.json();
            
            console.log('Geocoding response:', data);
            
            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                console.log('Setting coordinates:', lat, lon);
                setFormData(prev => ({
                    ...prev,
                    latitude: parseFloat(lat),
                    longitude: parseFloat(lon)
                }));
            } else {
                console.log('No results found for address');
            }
        } catch (error) {
            console.error('Geocoding error:', error);
        } finally {
            setIsGeocoding(false);
        }
    };

    const reverseGeocode = async (lat: number, lng: number) => {
        try {
            console.log('Reverse geocoding:', lat, lng);
            // Using Nominatim for reverse geocoding
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
                {
                    headers: {
                        'User-Agent': 'BarmenFinder/1.0'
                    }
                }
            );
            const data = await response.json();
            
            console.log('Reverse geocoding response:', data);
            
            if (data && data.display_name) {
                setFormData(prev => ({
                    ...prev,
                    location: data.display_name,
                    latitude: lat,
                    longitude: lng
                }));
            }
        } catch (error) {
            console.error('Reverse geocoding error:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const auth = ensureAuthenticated();
        if (!auth.allowed) {
            showToast({
                message: t('common.loginRequired') || 'Please log in to continue',
                type: 'error'
            });
            navigate(auth.redirectTo);
            return;
        }

        const token = auth.token;

        try {
            const jobData: any = {
                jobRole: formData.jobRole,
                facilityName: formData.facilityName || undefined,
                place: formData.location,
                payForTransport: formData.payForTransport,
                description: formData.description || undefined,
                payRate: formData.payRate || undefined
            };

            const parsedStaffCount = parseInt(formData.numberOfStaff, 10);
            if (!Number.isNaN(parsedStaffCount) && parsedStaffCount > 0) {
                jobData.numberOfStaff = parsedStaffCount;
            }

            // Add date and time fields based on selection
            if (dateType === 'singleDay') {
                const calculateDefaultEndTime = (startTime: string): string => {
                    const [hoursStr, minutesStr] = startTime.split(':');
                    const hours = Number(hoursStr);
                    const minutes = Number(minutesStr);

                    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
                        return '23:59';
                    }

                    const totalMinutes = hours * 60 + minutes + 8 * 60;
                    if (totalMinutes >= 24 * 60) {
                        return '23:59';
                    }

                    const endHours = Math.floor(totalMinutes / 60);
                    const endMinutes = totalMinutes % 60;
                    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
                };

                jobData.date = formData.date; // YYYY-MM-DD format
                jobData.startTime = formData.startTime; // HH:MM format
                jobData.endTime = formData.endTime || calculateDefaultEndTime(formData.startTime); // HH:MM format
            } else {
                jobData.startDate = formData.startDate; // YYYY-MM-DD format
                jobData.endDate = formData.endDate; // YYYY-MM-DD format
                // No time fields for period
            }

            console.log('Sending job request data:', jobData);

            const response = await fetch(getApiUrl('job-requests'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(jobData)
            });

            if (!response.ok) {
                const errorData = await readJsonSafe<any>(response);
                throw new Error(getApiErrorMessage(response, t('messages.errorOccurred') || 'Failed to create job offer', errorData));
            }

            const data = await response.json();
            console.log('Job offer created:', data);
            
            showToast({ message: t('messages.saveSuccess'), type: 'success' });
            setTimeout(() => navigate('/dashboard'), 1500);
        } catch (error) {
            console.error('Error creating job offer:', error);
            showToast({ 
                message: error instanceof Error ? error.message : t('messages.errorOccurred'), 
                type: 'error' 
            });
        }
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const handleMapClick = () => {
        setTempCoords({ lat: formData.latitude, lng: formData.longitude });
        setShowMapModal(true);
    };

    const handleCloseModal = () => {
        setShowMapModal(false);
    };

    const handleMapSelect = () => {
        // Update form data with temporary coordinates and get address
        reverseGeocode(tempCoords.lat, tempCoords.lng);
        setShowMapModal(false);
    };

    return (
        <div className={styles.createJobPage}>
            <header className={headerStyles.header}>
                <button className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`} onClick={handleBackClick}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={styles.headerTitle}>{t('createJobOffer.title')}</h1>
                <div></div>
            </header>

            <div className={pageStyles.container} style={{ backgroundImage: `url(${backgroundImage})` }}>
                <div className={styles.contentWrapper}>
                    <form onSubmit={handleSubmit} className={styles.jobForm}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="jobRole">{t('createJobOffer.position')}</label>
                            <select
                                id="jobRole"
                                name="jobRole"
                                value={formData.jobRole}
                                onChange={handleInputChange}
                                className={`${styles.formInput} ${styles.formSelect}`}
                                required
                            >
                                <option value="">{t('createJobOffer.selectPosition')}</option>
                                {staffRoles.map((role) => (
                                    <option key={role} value={role}>
                                        {t(`roles.${role.toLowerCase().replace(/\s+/g, '').replace('/', '')}`)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="facilityName">{t('createJobOffer.facilityName')}</label>
                            <input
                                type="text"
                                id="facilityName"
                                name="facilityName"
                                value={formData.facilityName}
                                onChange={handleInputChange}
                                className={`${styles.formInput}${(storedUser as { businessName?: string | null } | null)?.businessName ? ` ${styles.formInputDisabled}` : ''}`}
                                placeholder={t('createJobOffer.facilityPlaceholder')}
                                disabled={!!((storedUser as { businessName?: string | null } | null)?.businessName)}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="numberOfStaff">{t('createJobOffer.numberOfStaff')}</label>
                            <input
                                type="number"
                                id="numberOfStaff"
                                name="numberOfStaff"
                                value={formData.numberOfStaff}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                placeholder={t('createJobOffer.numberOfStaffPlaceholder')}
                                min={1}
                                step={1}
                                required
                            />
                        </div>

                        {/* Date Type Selection */}
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>{t('createJobOffer.dateType')}</label>
                            <div className={styles.dateTypeOptions}>
                                <label className={styles.radioOption}>
                                    <input
                                        type="radio"
                                        name="dateType"
                                        value="singleDay"
                                        checked={dateType === 'singleDay'}
                                        onChange={(e) => setDateType('singleDay')}
                                        className={styles.radioInput}
                                    />
                                    <span className={styles.radioLabel}>{t('createJobOffer.singleDay')}</span>
                                </label>
                                <label className={styles.radioOption}>
                                    <input
                                        type="radio"
                                        name="dateType"
                                        value="period"
                                        checked={dateType === 'period'}
                                        onChange={(e) => setDateType('period')}
                                        className={styles.radioInput}
                                    />
                                    <span className={styles.radioLabel}>{t('createJobOffer.period')}</span>
                                </label>
                            </div>
                        </div>

                        {/* Conditional Date Fields */}
                        {dateType === 'singleDay' ? (
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="date">{t('createJobOffer.date')}</label>
                                    <input
                                        type="date"
                                        id="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        className={styles.formInput}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="startTime">{t('createJobOffer.startTime')}</label>
                                    <input
                                        type="time"
                                        id="startTime"
                                        name="startTime"
                                        value={formData.startTime}
                                        onChange={handleInputChange}
                                        className={styles.formInput}
                                        required
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="startDate">{t('createJobOffer.startDate')}</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        className={styles.formInput}
                                        min={new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel} htmlFor="endDate">{t('createJobOffer.endDate')}</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleInputChange}
                                        className={styles.formInput}
                                        min={formData.startDate || new Date().toISOString().split('T')[0]}
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {/* Hidden endTime field for compatibility */}
                        {false && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel} htmlFor="endTime">{t('createJobOffer.endTime')}</label>
                                <input
                                    type="time"
                                    id="endTime"
                                    name="endTime"
                                    value={formData.endTime}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="location">
                                {t('createJobOffer.location')} {isGeocoding && <span className={styles.geocodingIndicator}>({t('createJobOffer.searching')})</span>}
                            </label>
                            <input
                                type="text"
                                id="location"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                placeholder={t('createJobOffer.locationPlaceholder')}
                                required
                            />
                        </div>

                        <div className={styles.mapContainer} onClick={handleMapClick}>
                            <div className={styles.mapOverlay}>
                                <span className={styles.mapClickText}>{t('createJobOffer.clickToSelect')}</span>
                            </div>
                            <iframe
                                key={`map-${formData.latitude}-${formData.longitude}`}
                                title="Google Maps"
                                src={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}&z=15&output=embed`}
                                width="100%"
                                height="100%"
                                style={{ border: 0, borderRadius: '12px' }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="description">{t('createJobOffer.jobDescription')}</label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                className={`${styles.formInput} ${styles.formTextarea}`}
                                rows={4}
                                placeholder={t('createJobOffer.descriptionPlaceholder')}
                                style={{ resize: 'vertical', minHeight: '100px' }}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="payRate">{t('createJobOffer.payRate')}</label>
                            <input
                                type="text"
                                id="payRate"
                                name="payRate"
                                value={formData.payRate}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                placeholder={t('createJobOffer.payRatePlaceholder')}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>{t('createJobOffer.payForTransport')}</label>
                            <div className={styles.checkboxWrapper}>
                                <input
                                    type="checkbox"
                                    name="payForTransport"
                                    checked={formData.payForTransport}
                                    onChange={handleInputChange}
                                    className={styles.checkboxInput}
                                />
                            </div>
                        </div>

                        <button type="submit" className={styles.submitButton}>
                            {t('createJobOffer.createOffer')}
                        </button>
                    </form>
                </div>
            </div>

            {/* Map Selection Modal */}
            {showMapModal && (
                <div className={styles.mapModal} onClick={handleCloseModal}>
                    <div className={styles.mapModalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.mapModalHeader}>
                            <h3>Select Location</h3>
                            <button className={styles.closeModalBtn} onClick={handleCloseModal}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className={styles.mapModalBody}>
                            <iframe
                                key={`modal-map-${tempCoords.lat}-${tempCoords.lng}`}
                                title="Interactive Map"
                                src={`https://www.google.com/maps?q=${tempCoords.lat},${tempCoords.lng}&z=15&output=embed`}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen
                                loading="lazy"
                            ></iframe>
                        </div>
                        <div className={styles.mapModalFooter}>
                            <div className={styles.coordsInputGroup}>
                                <div className={styles.coordInput}>
                                    <label>Latitude:</label>
                                    <input
                                        type="number"
                                        step="0.000001"
                                        value={tempCoords.lat}
                                        onChange={(e) => setTempCoords(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                                        className={styles.coordField}
                                    />
                                </div>
                                <div className={styles.coordInput}>
                                    <label>Longitude:</label>
                                    <input
                                        type="number"
                                        step="0.000001"
                                        value={tempCoords.lng}
                                        onChange={(e) => setTempCoords(prev => ({ ...prev, lng: parseFloat(e.target.value) || 0 }))}
                                        className={styles.coordField}
                                    />
                                </div>
                            </div>
                            <p className={styles.mapHint}>Enter coordinates or click map location</p>
                            <button className={styles.confirmLocationBtn} onClick={handleMapSelect}>
                                Confirm Location
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default CreateJobOffer;
